/**
 * Component: Quick view
 * ------------------------------------------------------------------------------
 * An quick view file that contains scripts to function.
 *
 * @namespace quickView
 */
import axios from 'axios';
import Cookies from 'js-cookie';

import loaders from '../helpers/loaders';
import {on, serialize} from '../helpers/utils';
import cssClasses from '../helpers/cssClasses';
import ProductSwatchesColors from './product-swatches-colors';
import Modal from './modal';
import ProductForm from './product-form';
import QuickViewGallery from './quickview-gallery';


/**
 * DOM selectors.
 */
const selectors = {
  addToCart: '[js-quick-view="addToCart"]',
  container: '[js-quick-view="container"]',
  viewport: '[js-quick-view="viewport"]',
  body: '[js-quick-view="body"]',
};

/**
 * Attribute and data selectors.
 */
const dataSelectors = {
  quickView: 'js-quick-view',
};

/**
 * Create a new quick view object.
 * @param {object} config - Settings object defined when instantiating.
 */
export default () => {

  const namespace = 'quick-view';

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    body: document.querySelector(selectors.body),
    container: [...document.querySelectorAll(selectors.container)],
    toggleSelectors: document.querySelector(selectors.toggleSelectors),
    viewport: document.querySelector(selectors.viewport),
  };

  /**
   * Global instances.
   */
  const modal = new Modal({
    id: namespace,
  });

  /**
   * Initialise component.
   */
  function init() {
    if (
      !nodeSelectors.container.length ||
      document.documentElement.classList.contains('quickview-loaded')
      ) {
      return;
    }

    setEventListeners();
    modal.init();
    document.documentElement.classList.add('quickview-loaded');
  }

  /**
   * Set trigger listeners.
   */
  function setEventListeners() {
    nodeSelectors.container.forEach((element) => {
      on('click', element, (event) => handleQuickViewClick(event));
    });
  }

  /**
   * Handle quick view toggle click event.
   * @param {Event} event - Click event.
   */
  function handleQuickViewClick(event) {
    if (!isTargetToggle(event.target)) {
      return;
    }

    event.preventDefault();

    let productHandle = event.target.dataset.productUrl;

    if (productHandle.includes('https://')) {
      /* eslint-disable-next-line */
      const url = new URL(productHandle);
      const handle = url.pathname;

      productHandle = handle;
    }

    productHandle = productHandle.replace('/products/', '');

    handleQuickViewEvent(productHandle);
  }

  /**
   * Handle quick view eventbus event.
   * @param {String} handle – The product handle to quick view.
   */
  function handleQuickViewEvent(handle) {
    setLoadingState();
    toggleQuickView(handle);
  }

  /**
   * Detect quick view toggle click.
   * @param {HTMLElement} target - Clicked target to check quick view attributes against.
   */
  function isTargetToggle(target) {
    return (
      typeof target.attributes[dataSelectors.quickView] !== 'undefined' &&
      target.attributes[dataSelectors.quickView].nodeValue === 'toggle'
    );
  }

  /**
   * Toggle quick view modal after fetching data from toggle target.
   * @param {String} handle – The product handle to quick view.
   */
  function toggleQuickView(handle) {
    modal.show(namespace);

    axios.get(getProductUrl(handle))
      .then((response) => {
        nodeSelectors.body.innerHTML = response.data;
        removeLoadingState();
        initProductForm();
        ProductSwatchesColors('quickView').init();
        setAddToCartEvents();
        QuickViewGallery().init();
        return response;
      })
      .catch((error) => error);
  }

  /**
   * Set loading state.
   */
  function setLoadingState() {
    nodeSelectors.viewport.classList.add(cssClasses.loading);
    nodeSelectors.body.innerHTML = loaders.ballPulse;
  }

  /**
   * Remove loading state.
   */
  function removeLoadingState() {
    nodeSelectors.viewport.classList.remove(cssClasses.loading);
  }

  /**
   * Initialise product form.
   */
  function initProductForm() {
    ProductForm({
      container: 'quick-view',
      enableSwatches: true,
      on: {
        variantChange: (variant) => {
          const shortSku = variant.sku.substring(0, variant.sku.length - 3);
          nodeSelectors.body.querySelector('[js-product="skuWrapper"]').classList.remove(cssClasses.hidden);
          nodeSelectors.body.querySelector('[js-product="sku"]').innerHTML = shortSku;
        },
      },
    }).init();

    if (theme.features.currencyConverter) {
      Frame.MultiCurrency.CurrencyConverter.convertAll(Cookies.get('new_currency'));
    }
  }

  /**
   * Set click events on buttons to post form data.
   */
  function setAddToCartEvents() {
    on('click', document.querySelector(selectors.addToCart), (event) => handleAddToCart(event));
  }

  /**
   * Handle add to cart event and set loading state.
   * @param {object} event - Click and key down event.
   */
  function handleAddToCart(event) {
    event.preventDefault();
    const target = event.currentTarget;
    setAddingToCartState(target);

    axios.post('/cart/add.js', serialize(target.closest('form')))
      .then((response) => {
        setSuccessState(target, response);
        return response;
      })
      .catch((error) => {
        return error;
      });
  }

  /**
   * Set success state when item has been successfully added.
   * @param {HTMLElement} target - Add to cart button.
   * @param {Object} response - Successful JSON response from add.js.
   */
  function setSuccessState(target, response) {
    setDefaultState(target);

    window.setTimeout(() => {
      Frame.EventBus.emit('AjaxCart:itemAdded', response);
    }, 500);

    modal.close(namespace);
  }

  /**
   * Reset the loading states back to default.
   * @param {object} target clicked add to cart button in DOM.
   */
  function setDefaultState(target) {
    target.innerText = theme.strings.addToCart;
  }

  /**
   * Set adding state to add to cart button.
   * @param {object} target clicked add to cart button in DOM.
   */
  function setAddingToCartState(target) {
    target.innerText = theme.strings.addingToCart;
  }

  /**
   * Get quick view product template url.
   * @param {String} handle – The product handle to quick view.
   */
  function getProductUrl(handle) {
    return `/products/${handle}?view=quick-view`;
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
};
